'use client';

import { useEffect, useState } from 'react';
import { styled } from '@linaria/react';
import FollowButton from '@fe-news/components/atoms/Member/FollowButton';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import useLoginModal from '@fe-news/hooks/use-login-modal';
import useMyCollection from '@fe-news/hooks/use-my-collection';
import useUpdateMyCollection from '@fe-news/hooks/use-update-my-collection';
import { CustomEventName, GTMEventKey } from '@fe-cnyes/fe-common-gtm';
import Modal from '@fe-news/components/molecules/Modal';
import LoginModal from '@fe-news/components/molecules/LoginModal';
import SuccessFollowPopUp from '@fe-news/components/molecules/SuccessFollowPopUp';

// types
import { MyCollectionProductType, LIMIT_POP_TOPIC_COUNT } from '@fe-news/constants/my-track/type';

// utils
import { isBookmark, handleFollow } from '@fe-news/utils/member';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 398px;

  @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
    min-width: 327px;
  }

  h3 {
    font-size: 18px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-align: center;
  }

  p {
    margin-top: 16px;
    font-size: 16px;
    letter-spacing: 0.5px;
    color: #383838;
    text-align: center;
  }

  button {
    margin-top: 24px;
    font-size: 14px;
    background-color: #e03f19;
    color: #fff;
    padding: 9px 14px;
    border-radius: 4px;
    cursor: pointer;
    align-self: center;
  }
`;

type PopTopicFollowButtonProps = {
  id: string;
  title: string;
};

const PopTopicFollowButton = ({ id, title }: PopTopicFollowButtonProps) => {
  const [isClose, setIsClose] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { isLoggedIn, onClickLoginModalOpen, isCloseLoginModal, onClickLoginModalClose } = useLoginModal({
    defaultCloseLoginModal: true
  });

  const {
    refetch: refetchMyCollection,
    myCollectionDataItemsIdArray,
    isLoading
  } = useMyCollection({
    productType: MyCollectionProductType.POPTOPIC,
    isLogin: isLoggedIn,
    isRankingList: true
  });

  const {
    postMyCollection,
    deleteMyCollection,
    isLoading: isUpdateMyCollectionLoading
  } = useUpdateMyCollection({
    refetchMyCollection
  });

  const isBookmarked = isBookmark(myCollectionDataItemsIdArray, id.toString());

  const handleDelete = async () => {
    const response = await deleteMyCollection({
      productType: MyCollectionProductType.POPTOPIC,
      data: [id.toString()]
    });
    return response.message ?? '';
  };

  const handlePost = async () => {
    const response = await postMyCollection({
      productType: MyCollectionProductType.POPTOPIC,
      data: [id.toString()]
    });
    return response.message ?? '';
  };

  const onClickFollow = () => {
    handleFollow({
      isLoggedIn,
      isBookmarked,
      newsId: id.toString(),
      dataLength: myCollectionDataItemsIdArray?.length,
      limit: LIMIT_POP_TOPIC_COUNT,
      productType: MyCollectionProductType.POPTOPIC,
      setIsSaved,
      setIsClose,
      setIsSuccess,
      handleDelete,
      handlePost,
      onClickLoginModalOpen,
      gtmEvent: {
        [GTMEventKey.EVENT_NAME]: CustomEventName.FOLLOW_POP,
        [GTMEventKey.SECTION]: 'News_Category_我的追蹤',
        [GTMEventKey.CLICK_ITEM]: `追蹤的時事＿熱門排行＿${title}`
      }
    });
  };

  const onClose = () => {
    setIsClose(true);
  };

  useEffect(() => {
    setIsSaved(isBookmarked);
  }, [isBookmarked]);

  return (
    <>
      {isSuccess && <SuccessFollowPopUp setIsSuccess={setIsSuccess} />}
      <LoginModal isModalClosed={isCloseLoginModal} onClickModalClose={onClickLoginModalClose} />
      <Modal isClose={isClose} onClose={onClose}>
        <Content>
          <h3>提醒您</h3>
          <p>熱門時事追蹤數量已達上限 10 則</p>
          <button onClick={onClose}>返回</button>
        </Content>
      </Modal>
      <FollowButton
        onClickTrack={onClickFollow}
        isTracked={isSaved}
        isLoading={isUpdateMyCollectionLoading || isLoading}
      />
    </>
  );
};

export default PopTopicFollowButton;
